import { useMemo, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Train,
  ArrowLeft,
  Search,
  Zap,
  AlertTriangle,
  Info,
  Filter,
  X,
  BookOpen,
  ShieldAlert,
  Clock,
} from "lucide-react";
import PnjLogo from "@/components/PnjLogo";
import data from "@/data/rollingstock_guidance.json";

/**
 * RollingstockGuidance
 * Halaman detail modul Rollingstock — pencarian abbreviation → detail.
 * Sumber data: `data/rollingstock_guidance.json` yang di-generate dari
 * "Guidance list - FAILURE CODE TIS (ROLLING STOCK) REV.02.xlsx" (sheet
 * Master Clear). 316 entries × 15 sub-system.
 */
const ENTRIES = data.entries || [];
const SYSTEMS = Array.from(
  new Set(ENTRIES.map((e) => e.system).filter(Boolean)),
).sort();

const highlight = (text, needle) => {
  if (!needle) return text;
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-[#0ea5e9]/30 text-white rounded px-0.5">
        {text.slice(idx, idx + needle.length)}
      </span>
      {text.slice(idx + needle.length)}
    </>
  );
};

const SeverityBadge = ({ entry }) => {
  const badges = [];
  if (entry.heavy === "1")
    badges.push({
      key: "heavy",
      label: "Heavy",
      cls: "bg-[#CA2047]/20 text-[#ff8fa3] border-[#CA2047]/40",
    });
  if (entry.light === "1")
    badges.push({
      key: "light",
      label: "Light",
      cls: "bg-[#f59e0b]/20 text-[#fbbf24] border-[#f59e0b]/40",
    });
  if (entry.impossible_run === "1")
    badges.push({
      key: "imp",
      label: "Impossible to Run",
      cls: "bg-[#7c3aed]/20 text-[#c4b5fd] border-[#7c3aed]/40",
    });
  if (badges.length === 0)
    badges.push({
      key: "info",
      label: "Info",
      cls: "bg-slate-500/20 text-slate-300 border-slate-500/40",
    });
  return (
    <div className="flex flex-wrap gap-1.5">
      {badges.map((b) => (
        <span
          key={b.key}
          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${b.cls}`}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
};

const DetailPanel = ({ entry }) => {
  if (!entry) return null;
  return (
    <div
      data-testid="detail-panel"
      className="rounded-xl border border-[#0ea5e9]/40 bg-gradient-to-br from-[#0d243a] to-[#062036] p-4 md:p-5 shadow-lg shadow-cyan-500/10"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#7dd3fc]">
            {entry.system} · Code {entry.code}
          </div>
          <h3 className="mt-0.5 font-display text-lg md:text-xl font-bold leading-tight">
            {entry.name || entry.abbr}
          </h3>
          <div className="mt-0.5 text-[11px] font-mono text-slate-400">
            Abbreviation:{" "}
            <span className="text-[#facc15] font-bold text-sm">
              {entry.abbr}
            </span>
          </div>
        </div>
        <SeverityBadge entry={entry} />
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-[#243a52] bg-[#0b1a29] p-3">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#f472b6] mb-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            Cause
          </div>
          <div className="text-xs md:text-sm text-slate-200 leading-relaxed whitespace-pre-line">
            {entry.cause || "—"}
          </div>
        </div>

        <div className="rounded-lg border border-[#22c55e]/30 bg-[#062036] p-3">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#22c55e] mb-1.5">
            <BookOpen className="h-3.5 w-3.5" />
            Guidance / Handling
          </div>
          <div className="text-xs md:text-sm text-slate-100 leading-relaxed whitespace-pre-line">
            {entry.guidance || "—"}
          </div>
        </div>
      </div>

      {(entry.record || entry.screen_indicator) && (
        <div className="mt-3 flex flex-wrap gap-4 text-[10px] text-slate-400">
          {entry.record && (
            <div>
              <span className="uppercase tracking-widest text-slate-500">
                Record:
              </span>{" "}
              <span className="text-slate-200">{entry.record}</span>
            </div>
          )}
          {entry.screen_indicator && (
            <div>
              <span className="uppercase tracking-widest text-slate-500">
                Screen indicator:
              </span>{" "}
              <span className="text-slate-200">{entry.screen_indicator}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function RollingstockGuidance() {
  const [query, setQuery] = useState("");
  const [systemFilter, setSystemFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [recent, setRecent] = useState([]);

  // Load recent searches (abbreviations) from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem("rs_recent_abbr");
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setRecent(arr.slice(0, 5));
      }
    } catch {
      /* ignore */
    }
  }, []);

  const pushRecent = useCallback((abbr) => {
    if (!abbr) return;
    setRecent((prev) => {
      const next = [abbr, ...prev.filter((x) => x !== abbr)].slice(0, 5);
      try {
        localStorage.setItem("rs_recent_abbr", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const clearRecent = () => {
    setRecent([]);
    try {
      localStorage.removeItem("rs_recent_abbr");
    } catch {
      /* ignore */
    }
  };

  const hasFilter = systemFilter !== "ALL";
  const hasQuery = query.trim().length > 0;

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = ENTRIES;
    if (hasFilter) {
      list = list.filter((e) => e.system === systemFilter);
    }
    if (!q) {
      // Filter-only browse mode: tampilkan seluruh entries dari sistem terpilih
      // (default sort by code). Batasi 100 untuk performa render.
      return list
        .slice()
        .sort((a, b) => String(a.code).localeCompare(String(b.code)))
        .slice(0, 100);
    }
    // Score matches: abbreviation prefix > abbreviation contains > name/cause contains
    const scored = list
      .map((e) => {
        const abbr = e.abbr.toLowerCase();
        const name = (e.name || "").toLowerCase();
        const cause = (e.cause || "").toLowerCase();
        let score = 0;
        if (abbr === q) score = 100;
        else if (abbr.startsWith(q)) score = 80;
        else if (abbr.includes(q)) score = 60;
        else if (name.includes(q)) score = 40;
        else if (cause.includes(q)) score = 20;
        return { e, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    return scored.map((x) => x.e).slice(0, 30);
  }, [query, systemFilter, hasFilter]);

  const perfectMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return ENTRIES.find((e) => e.abbr.toLowerCase() === q) || null;
  }, [query]);

  const active = selected || perfectMatch;

  // Tercatat sebagai recent saat user membuka detail (klik entry / match sempurna)
  useEffect(() => {
    if (active?.abbr) pushRecent(active.abbr);
  }, [active, pushRecent]);

  // Klik chip recent → langsung set query ke abbr tsb.
  const applyRecent = (abbr) => {
    setQuery(abbr);
    setSelected(null);
  };

  // Idle state = tidak ada query, tidak ada filter, tidak ada entry aktif.
  const isIdle = !hasQuery && !hasFilter && !active;
  // Results section muncul bila ada query ATAU filter dipilih.
  const showResults = hasQuery || hasFilter;

  return (
    <div
      className="min-h-screen bg-[#0c1b2a] text-white relative overflow-hidden"
      data-testid="page-rollingstock-guidance"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-25 rs-grid" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.14),transparent_55%)]" />
      </div>

      <header className="relative z-10 max-w-6xl mx-auto flex items-center justify-between px-6 pt-8">
        <Link
          to="/failure-guidance"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Failure Guidance
        </Link>
        <PnjLogo size={32} />
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-5 pb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0ea5e9] to-[#22c55e] flex items-center justify-center shadow-lg shadow-cyan-500/30">
            <Train className="h-6 w-6 text-[#0c1b2a]" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#7dd3fc]">
              Rollingstock · {ENTRIES.length} entries
            </div>
            <h1 className="font-display text-xl md:text-2xl font-extrabold leading-tight">
              Rollingstock Guidance List
            </h1>
          </div>
        </div>

        {/* Search + filter */}
        <div className="rounded-xl border border-[#243a52] bg-[#0b1a29]/70 p-3 md:p-4">
          <div className="flex flex-col md:flex-row gap-2.5">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelected(null);
                }}
                placeholder="Ketik singkatan failure (mis. DDUAT, VVVFAT, TCMS)..."
                data-testid="rs-search-input"
                className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-[#0c1b2a] border border-[#243a52] focus:border-[#0ea5e9] focus:outline-none focus:ring-1 focus:ring-[#0ea5e9] text-sm font-mono placeholder:text-slate-500"
                autoFocus
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#243a52]"
                  aria-label="Clear"
                  data-testid="rs-clear"
                >
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              )}
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <select
                value={systemFilter}
                onChange={(e) => setSystemFilter(e.target.value)}
                data-testid="rs-system-filter"
                className="w-full md:w-44 pl-10 pr-4 py-2.5 rounded-lg bg-[#0c1b2a] border border-[#243a52] focus:border-[#0ea5e9] focus:outline-none text-sm appearance-none cursor-pointer"
              >
                <option value="ALL">Semua sistem</option>
                {SYSTEMS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400">
            <Info className="h-3 w-3" />
            <span>
              Ketik <span className="font-mono text-[#facc15]">abbreviation</span>{" "}
              lengkap untuk detail langsung, atau ketik sebagian untuk daftar
              usulan.
            </span>
          </div>
        </div>

        {/* Detail (perfect match / selected) */}
        {active && (
          <div className="mt-4">
            <DetailPanel entry={active} />
          </div>
        )}

        {/* Result list — muncul bila ada query ATAU filter dipilih */}
        {showResults && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
              {hasQuery
                ? `Hasil pencarian (${results.length})`
                : `${systemFilter} · ${results.length} entries`}
            </div>

            {results.length === 0 && (
              <div
                className="rounded-lg border border-dashed border-[#CA2047]/40 bg-[#CA2047]/5 p-4 flex items-start gap-3"
                data-testid="rs-empty"
              >
                <ShieldAlert className="h-5 w-5 text-[#ff8fa3] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#ff8fa3] text-sm">
                    Abbreviation tidak ditemukan
                  </div>
                  <div className="text-xs text-slate-300 mt-0.5">
                    Coba ubah kata kunci atau ganti filter sistem. Bisa juga cari
                    lewat kata dari nama atau cause.
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-1.5" data-testid="rs-results">
              {results.map((e) => {
                const isActive = active && active.abbr === e.abbr;
                return (
                  <button
                    key={`${e.system}-${e.code}-${e.abbr}`}
                    onClick={() => setSelected(e)}
                    data-testid={`rs-result-${e.abbr}`}
                    className={`text-left rounded-lg border px-3 py-2 transition-all hover:-translate-y-0.5 ${
                      isActive
                        ? "border-[#0ea5e9]/70 bg-[#062036] shadow-md shadow-cyan-500/10"
                        : "border-[#243a52] bg-[#0b1a29]/70 hover:border-[#0ea5e9]/40"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-[#facc15] text-sm">
                            {highlight(e.abbr, query)}
                          </span>
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#122032] text-slate-400">
                            {e.system}
                          </span>
                          <span className="text-[9px] font-mono text-slate-500">
                            #{e.code}
                          </span>
                        </div>
                        <div className="mt-0.5 text-xs text-slate-200 truncate">
                          {highlight(e.name || "—", query)}
                        </div>
                      </div>
                      <SeverityBadge entry={e} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Idle: prompt + recent searches chips */}
        {isIdle && (
          <div
            className="mt-4 rounded-lg border border-dashed border-[#243a52] bg-[#0b1a29]/50 p-6"
            data-testid="rs-idle"
          >
            <div className="text-center">
              <Search className="h-6 w-6 text-slate-500 mx-auto mb-2" />
              <div className="text-sm text-slate-300">
                Mulai mengetik atau pilih sistem untuk mencari{" "}
                <span className="font-mono text-[#facc15]">abbreviation</span>{" "}
                failure code
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {ENTRIES.length} entries · {SYSTEMS.length} sub-system
              </div>
            </div>

            {recent.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[#243a52]" data-testid="rs-recent">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
                    <Clock className="h-3 w-3" />
                    Recent Searches
                  </div>
                  <button
                    onClick={clearRecent}
                    data-testid="rs-recent-clear"
                    className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#ff8fa3]"
                  >
                    Bersihkan
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recent.map((abbr) => (
                    <button
                      key={abbr}
                      onClick={() => applyRecent(abbr)}
                      data-testid={`rs-recent-${abbr}`}
                      className="text-xs font-mono px-2.5 py-1 rounded-full border border-[#0ea5e9]/40 bg-[#0ea5e9]/10 text-[#7dd3fc] hover:bg-[#0ea5e9]/20 transition-colors"
                    >
                      {abbr}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <footer className="mt-10 pt-4 border-t border-[#243a52] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[11px] text-slate-500">
          <div>
            <span className="text-slate-400 font-display font-semibold">
              CRLS Console Suite
            </span>{" "}
            · Rollingstock Guidance · v1.0
          </div>
          <PnjLogo size={28} />
        </footer>
      </main>

      <style>{`
        .rs-grid {
          background-image:
            linear-gradient(rgba(14,165,233,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14,165,233,0.05) 1px, transparent 1px);
          background-size: 42px 42px;
        }
      `}</style>
    </div>
  );
}
